import React, { useState } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  InputGroup,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { API } from "../../constants";
import useAuthStore from "../../hooks/useAuthStore";

const ChangePassword = () => {
  const navigate = useNavigate();
  const { clearUser } = useAuthStore();

  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const handleChange = (e) => {
    setPasswords({
      ...passwords,
      [e.target.name]: e.target.value,
    });

    setMessage({ type: "", text: "" });
  };

  const togglePassword = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field],
    });
  };

  const logoutAfterPasswordChange = async () => {
    try {
      await fetch(API.CUSTOMER_LOGOUT, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout after password change error:", error);
    } finally {
      clearUser();

      setTimeout(() => {
        navigate("/login");
      }, 1000);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setMessage({ type: "", text: "" });

    if (!passwords.currentPassword.trim()) {
      setMessage({
        type: "danger",
        text: "Current password is required.",
      });
      return;
    }

    if (!passwords.newPassword.trim()) {
      setMessage({
        type: "danger",
        text: "New password is required.",
      });
      return;
    }

    if (passwords.newPassword.length < 6) {
      setMessage({
        type: "danger",
        text: "New password must be at least 6 characters.",
      });
      return;
    }

    if (!passwords.confirmPassword.trim()) {
      setMessage({
        type: "danger",
        text: "Confirm password is required.",
      });
      return;
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      setMessage({
        type: "danger",
        text: "New password and confirm password do not match.",
      });
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(API.CUSTOMER_CHANGE_PASSWORD, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          oldPassword: passwords.currentPassword,
          newPassword: passwords.newPassword,
          confirmPassword: passwords.confirmPassword,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.status === false) {
        setMessage({
          type: "danger",
          text: result.message || "Failed to change password.",
        });
        return;
      }

      setMessage({
        type: "success",
        text:
          result.message ||
          "Password changed successfully. Please login again.",
      });

      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setShowPassword({
        current: false,
        new: false,
        confirm: false,
      });

      await logoutAfterPasswordChange();
    } catch (error) {
      console.error("Change password error:", error);

      setMessage({
        type: "danger",
        text: "Network error. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-edit-profile-from">
      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          {message.text && (
            <Col md={12}>
              <Alert variant={message.type}>{message.text}</Alert>
            </Col>
          )}

          <Col md={12}>
            <Form.Group className="dash-form-input">
              <Form.Label>Current Password</Form.Label>

              <InputGroup>
                <Form.Control
                  type={showPassword.current ? "text" : "password"}
                  placeholder="Current Password"
                  name="currentPassword"
                  value={passwords.currentPassword}
                  onChange={handleChange}
                  required
                />

                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => togglePassword("current")}
                >
                  <i
                    className={`fa-solid ${
                      showPassword.current ? "fa-eye" : "fa-eye-slash"
                    }`}
                  ></i>
                </Button>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group className="dash-form-input">
              <Form.Label>New Password</Form.Label>

              <InputGroup>
                <Form.Control
                  type={showPassword.new ? "text" : "password"}
                  placeholder="New Password"
                  name="newPassword"
                  value={passwords.newPassword}
                  onChange={handleChange}
                  required
                />

                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => togglePassword("new")}
                >
                  <i
                    className={`fa-solid ${
                      showPassword.new ? "fa-eye" : "fa-eye-slash"
                    }`}
                  ></i>
                </Button>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group className="dash-form-input">
              <Form.Label>Confirm Password</Form.Label>

              <InputGroup>
                <Form.Control
                  type={showPassword.confirm ? "text" : "password"}
                  placeholder="Confirm Password"
                  name="confirmPassword"
                  value={passwords.confirmPassword}
                  onChange={handleChange}
                  required
                />

                <Button
                  variant="outline-secondary"
                  type="button"
                  onClick={() => togglePassword("confirm")}
                >
                  <i
                    className={`fa-solid ${
                      showPassword.confirm ? "fa-eye" : "fa-eye-slash"
                    }`}
                  ></i>
                </Button>
              </InputGroup>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Button
              type="submit"
              variant="primary"
              className="w-100"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save Now"
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default ChangePassword;